import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const FormInput = ({
  id,
  name,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  icon: Icon, // Rename prop to avoid conflict with component name
  containerClassName = '',
  error = null, // Pass error message or boolean for styling
  children, // For extra content like password strength
  ...props // Pass any other input props like autoComplete
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const currentType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const togglePasswordVisibility = () => {
    if (isPassword) {
      setShowPassword(!showPassword);
    }
  };

  // Base classes
  const baseInputClasses = "pl-10 py-3 w-full form-input rounded-xl bg-gray-50 border shadow-sm transition-all";
  const baseFocusClasses = "focus:outline-none focus:ring-2 focus:border-transparent";
  
  // Conditional classes based on error state
  const borderClasses = error ? 'border-red-300' : 'border-gray-200';
  const focusRingClasses = error ? 'focus:ring-red-500' : 'focus:ring-primary-500';
  
  // Combine classes
  const inputClassName = `${baseInputClasses} ${borderClasses} ${baseFocusClasses} ${focusRingClasses} ${isPassword ? 'pr-10' : ''}`;

  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-gray-400" />
          </div>
        )}
        <input
          id={id}
          name={name}
          type={currentType}
          required={required}
          className={inputClassName}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          {...props} 
        />
        {isPassword && (
          <button 
            type="button" 
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        )}
      </div>
      {/* Render children below the input (e.g., password strength, error message) */}
      {children} 
      {/* Display error message if provided as a string */}
      {typeof error === 'string' && error && (
         <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

export default FormInput;
